/** @file Per-track display maps (identity, or key range onto [0, 1]) and the graph range for frame-all. */
import { curveSegment } from './curveSegment';
import type { CurveTrack, CurveTrackMap } from './curveTypes';

const IDENTITY: CurveTrackMap = { offset: 0, gain: 1 };
const FIT_SAMPLES = 8;

/** [min, max] of a list (a loop: tracks can hold more keys than a spread call takes arguments). */
function rangeOf(values: readonly number[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of values) {
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
  }
  return [lo, hi];
}

/** Widens range by one track's keys and FIT_SAMPLES samples per segment (overshoot included), in graph units. */
function widen(track: CurveTrack, map: CurveTrackMap, range: [number, number]): void {
  const see = (v: number): void => {
    const g = (v - map.offset) * map.gain;
    range[0] = Math.min(range[0], g);
    range[1] = Math.max(range[1], g);
  };
  for (let i = 0; i < track.frames.length; i++) {
    see(curveSegment.ends(track, i)[0]);
    if (i === track.frames.length - 1) break;
    for (let k = 1; k <= FIT_SAMPLES; k++) see(curveSegment.valueAt(track, i, k / FIT_SAMPLES));
  }
}

/** Display maps of the graph: g = (value − offset) · gain. */
export const curveMaps = {
  /** Value mode and progress tracks keep values; normalized value tracks map their key range onto [0, 1] (flat: 0.5). */
  of(track: CurveTrack, normalized: boolean): CurveTrackMap {
    if (!normalized || track.mode === 'progress') return IDENTITY;
    const [lo, hi] = rangeOf(track.values);
    return hi - lo > 1e-9 ? { offset: lo, gain: 1 / (hi - lo) } : { offset: lo - 0.5, gain: 1 };
  },
  /** Graph-unit range covered by the tracks' keys and sampled curves; [0, 1] when there is no track. */
  bounds(tracks: readonly CurveTrack[], maps: readonly CurveTrackMap[]): [number, number] {
    const range: [number, number] = [Infinity, -Infinity];
    tracks.forEach((track, t) => {
      widen(track, maps[t] ?? IDENTITY, range);
    });
    return range[0] <= range[1] ? range : [0, 1];
  },
  /** First and last key frame over the tracks; [0, 30] when there is no track. */
  frames(tracks: readonly CurveTrack[]): [number, number] {
    const [lo, hi] = rangeOf(tracks.flatMap((track) => [track.frames[0] ?? 0, track.frames.at(-1) ?? 0]));
    return lo <= hi ? [lo, hi] : [0, 30];
  },
};
